import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { fetchAdminAnalytics, setUserDashboardAccess } from './supabaseClient';

const DASHBOARD_OPTIONS = [
  { key: 'funnel', label: 'Funnel Report' },
  { key: 'hiring', label: 'Hiring Forecast' },
  { key: 'marketing', label: 'Marketing Metrics' },
  { key: 'duration', label: 'Program Duration' },
  { key: 'partnerships', label: 'Partnerships' },
  { key: 'email', label: 'Monthly Email' },
  { key: 'sfquery', label: 'SF Query' },
  { key: 'testaudit', label: 'Test Entry Audit' },
];

const CARD_LABELS = {
  'ai-summary': 'AI Executive Summary',
  'kpi-cards': 'KPI Performance Cards',
  'highlights': 'Pacing & Highlights',
  'app-age': 'Applications Age Distribution',
  'cohort': 'Cohort Conversion Analysis',
  'app-trend': 'Application Trend (3-Year)',
  'enroll-trend': 'Enrollment Trend (3-Year)',
  'state-matrix': 'State Performance Matrix',
  'state-mom': 'State Enrollment Trends (MoM)',
  'hired-comparison': 'Hired Candidate Comparison',
  'eval-health': 'Evaluation Health',
  'forecast-readiness': 'Forecast Readiness',
};

const SUPERADMIN_EMAIL = 'andrew.rozell@iteach.net';

const AdminPanel = ({ onClose, currentUserEmail }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30); // days

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await fetchAdminAnalytics();
      setData(result);
      setLoading(false);
    };
    load();
  }, []);

  const filteredActivity = useMemo(() => {
    if (!data?.activity) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - timeRange);
    return data.activity.filter(a => new Date(a.created_at) >= cutoff);
  }, [data, timeRange]);

  const analytics = useMemo(() => {
    if (!data) return null;
    const activity = filteredActivity;
    const { users, preferences } = data;

    // 1. Who's using it and how often
    const userActivity = {};
    activity.forEach(a => {
      if (!userActivity[a.user_email]) userActivity[a.user_email] = { actions: 0, logins: 0, lastSeen: null };
      userActivity[a.user_email].actions++;
      if (a.action === 'login') userActivity[a.user_email].logins++;
      const ts = new Date(a.created_at);
      if (!userActivity[a.user_email].lastSeen || ts > userActivity[a.user_email].lastSeen) {
        userActivity[a.user_email].lastSeen = ts;
      }
    });

    const activeUsers = Object.entries(userActivity)
      .map(([email, stats]) => {
        const user = users.find(u => u.email === email);
        return {
          email,
          name: user?.display_name || email.split('@')[0],
          avatar: user?.avatar_url,
          actions: stats.actions,
          logins: stats.logins,
          lastSeen: stats.lastSeen,
          daysSinceSeen: Math.floor((new Date() - stats.lastSeen) / (1000 * 60 * 60 * 24)),
        };
      })
      .sort((a, b) => b.actions - a.actions);

    // 2. Most viewed states
    const stateCounts = {};
    activity.filter(a => a.action === 'filter_change' && a.details?.state).forEach(a => {
      const state = a.details.state;
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    });
    const topStates = Object.entries(stateCounts)
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // 3. PDF vs screen
    const pdfExports = activity.filter(a => a.action === 'pdf_export').length;
    const pageViews = activity.filter(a => a.action === 'page_view').length;

    // 4. Time of day distribution
    const hourCounts = Array(24).fill(0);
    activity.filter(a => a.action === 'page_view' || a.action === 'login').forEach(a => {
      const hour = new Date(a.created_at).getHours();
      hourCounts[hour]++;
    });
    const hourData = hourCounts.map((count, hour) => ({
      hour: `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}${hour < 12 ? 'a' : 'p'}`,
      visits: count,
    }));

    // 5. Hidden cards analysis
    const hiddenCounts = {};
    (preferences || []).forEach(pref => {
      if (pref.card_config && Array.isArray(pref.card_config)) {
        pref.card_config.forEach(card => {
          if (!card.visible) {
            hiddenCounts[card.id] = (hiddenCounts[card.id] || 0) + 1;
          }
        });
      }
    });
    const hiddenCards = Object.entries(hiddenCounts)
      .map(([id, count]) => ({ card: CARD_LABELS[id] || id, count }))
      .sort((a, b) => b.count - a.count);

    // 6. Session duration estimation
    // Group activity by user and day, measure span from first to last action
    const sessions = {};
    activity.forEach(a => {
      const day = new Date(a.created_at).toISOString().slice(0, 10);
      const key = `${a.user_email}|${day}`;
      if (!sessions[key]) sessions[key] = { first: new Date(a.created_at), last: new Date(a.created_at), actions: 0 };
      const ts = new Date(a.created_at);
      if (ts < sessions[key].first) sessions[key].first = ts;
      if (ts > sessions[key].last) sessions[key].last = ts;
      sessions[key].actions++;
    });
    const sessionDurations = Object.values(sessions)
      .map(s => ({ durationMin: Math.round((s.last - s.first) / 60000), actions: s.actions }))
      .filter(s => s.actions > 1); // Only sessions with multiple actions
    const avgSessionMin = sessionDurations.length > 0
      ? Math.round(sessionDurations.reduce((sum, s) => sum + s.durationMin, 0) / sessionDurations.length)
      : 0;
    const sessionBuckets = [
      { label: '< 1 min', count: sessionDurations.filter(s => s.durationMin < 1).length },
      { label: '1-5 min', count: sessionDurations.filter(s => s.durationMin >= 1 && s.durationMin < 5).length },
      { label: '5-15 min', count: sessionDurations.filter(s => s.durationMin >= 5 && s.durationMin < 15).length },
      { label: '15-30 min', count: sessionDurations.filter(s => s.durationMin >= 15 && s.durationMin < 30).length },
      { label: '30+ min', count: sessionDurations.filter(s => s.durationMin >= 30).length },
    ];

    // 7. Daily active users trend
    const dailyUsers = {};
    activity.forEach(a => {
      const day = new Date(a.created_at).toISOString().slice(0, 10);
      if (!dailyUsers[day]) dailyUsers[day] = new Set();
      dailyUsers[day].add(a.user_email);
    });
    const dailyTrend = Object.entries(dailyUsers)
      .map(([date, users]) => ({ date: date.slice(5), users: users.size }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // 8. Engagement dropoff - users who haven't been seen in 14+ days
    const droppedOff = users
      .filter(u => {
        const lastLogin = new Date(u.last_login);
        const daysSince = Math.floor((new Date() - lastLogin) / (1000 * 60 * 60 * 24));
        return daysSince >= 14 && u.login_count > 1;
      })
      .map(u => ({
        email: u.email,
        name: u.display_name || u.email.split('@')[0],
        lastLogin: new Date(u.last_login),
        daysSince: Math.floor((new Date() - new Date(u.last_login)) / (1000 * 60 * 60 * 24)),
        totalLogins: u.login_count,
      }))
      .sort((a, b) => b.daysSince - a.daysSince);

    // Action breakdown for pie chart
    const actionCounts = {};
    activity.forEach(a => {
      const label = {
        'login': 'Login',
        'page_view': 'Page View',
        'filter_change': 'Filter Change',
        'pdf_export': 'PDF Export',
        'data_refresh': 'Refresh',
        'card_toggle': 'Card Toggle',
        'card_reorder': 'Card Reorder',
        'logout': 'Logout',
      }[a.action] || a.action;
      actionCounts[label] = (actionCounts[label] || 0) + 1;
    });
    const actionBreakdown = Object.entries(actionCounts)
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count);

    return {
      activeUsers,
      topStates,
      pdfExports,
      pageViews,
      hourData,
      hiddenCards,
      avgSessionMin,
      sessionBuckets,
      dailyTrend,
      droppedOff,
      actionBreakdown,
      totalUsers: users.length,
      totalActions: activity.length,
    };
  }, [data, filteredActivity]);

  const COLORS = ['#00BEA8', '#CFFB5E', '#4BD48D', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h2 className="admin-title">📊 Usage Analytics</h2>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
          Loading analytics...
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h2 className="admin-title">📊 Usage Analytics</h2>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', padding: '2rem' }}>Unable to load analytics data.</p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2 className="admin-title">📊 Usage Analytics</h2>
        <div className="admin-header-right">
          <div className="admin-time-toggle">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                className={`admin-time-btn ${timeRange === d ? 'active' : ''}`}
                onClick={() => setTimeRange(d)}
              >
                {d}d
              </button>
            ))}
          </div>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Summary Stats Row */}
      <div className="admin-stats-row">
        <div className="admin-stat">
          <div className="admin-stat-value">{analytics.totalUsers}</div>
          <div className="admin-stat-label">Total Users</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-value">{analytics.activeUsers.length}</div>
          <div className="admin-stat-label">Active ({timeRange}d)</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-value">{analytics.totalActions}</div>
          <div className="admin-stat-label">Actions ({timeRange}d)</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-value">{analytics.pdfExports}</div>
          <div className="admin-stat-label">PDF Exports</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-value">{analytics.avgSessionMin}m</div>
          <div className="admin-stat-label">Avg Session</div>
        </div>
      </div>

      <div className="admin-grid">
        {/* Active Users Table */}
        <div className="admin-card admin-card-wide">
          <h3 className="admin-card-title">👤 Active Users</h3>
          <div className="admin-table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Actions</th>
                  <th>Logins</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {analytics.activeUsers.map(user => (
                  <tr key={user.email}>
                    <td>
                      <div className="admin-user-cell">
                        {user.avatar ? (
                          <img src={user.avatar} alt="" className="admin-user-avatar" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="admin-user-avatar admin-avatar-placeholder">{user.name.charAt(0)}</div>
                        )}
                        <div>
                          <div className="admin-user-name">{user.name}</div>
                          <div className="admin-user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="admin-td-num">{user.actions}</td>
                    <td className="admin-td-num">{user.logins}</td>
                    <td className="admin-td-light">
                      {user.daysSinceSeen === 0 ? 'Today' : user.daysSinceSeen === 1 ? 'Yesterday' : `${user.daysSinceSeen}d ago`}
                    </td>
                  </tr>
                ))}
                {analytics.activeUsers.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', opacity: 0.4 }}>No activity in this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Daily Active Users Chart */}
        <div className="admin-card admin-card-wide">
          <h3 className="admin-card-title">📈 Daily Active Users</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={analytics.dailyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
              <YAxis stroke="rgba(255,255,255,0.3)" allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: 'rgba(30,27,75,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.8rem' }} />
              <Line type="monotone" dataKey="users" stroke="#00BEA8" strokeWidth={2} dot={{ r: 3, fill: '#00BEA8' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top States */}
        <div className="admin-card">
          <h3 className="admin-card-title">🗺️ Most Viewed States</h3>
          {analytics.topStates.length > 0 ? (
            <div className="admin-bar-list">
              {analytics.topStates.map((s, i) => (
                <div key={s.state} className="admin-bar-item">
                  <span className="admin-bar-label">{s.state}</span>
                  <div className="admin-bar-track">
                    <div
                      className="admin-bar-fill"
                      style={{ width: `${(s.count / analytics.topStates[0].count) * 100}%` }}
                    />
                  </div>
                  <span className="admin-bar-count">{s.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="admin-empty">No filter changes recorded</p>
          )}
        </div>

        {/* Time of Day */}
        <div className="admin-card">
          <h3 className="admin-card-title">🕐 Peak Usage Hours</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={analytics.hourData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="hour" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 9 }} interval={2} />
              <YAxis stroke="rgba(255,255,255,0.3)" allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: 'rgba(30,27,75,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.8rem' }} />
              <Bar dataKey="visits" fill="#00BEA8" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Session Duration */}
        <div className="admin-card">
          <h3 className="admin-card-title">⏱️ Session Durations</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={analytics.sessionBuckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
              <YAxis stroke="rgba(255,255,255,0.3)" allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: 'rgba(30,27,75,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.8rem' }} />
              <Bar dataKey="count" fill="#CFFB5E" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Action Breakdown */}
        <div className="admin-card">
          <h3 className="admin-card-title">🎯 Action Breakdown</h3>
          <div className="admin-bar-list">
            {analytics.actionBreakdown.map((a, i) => (
              <div key={a.action} className="admin-bar-item">
                <span className="admin-bar-label">{a.action}</span>
                <div className="admin-bar-track">
                  <div
                    className="admin-bar-fill"
                    style={{
                      width: `${(a.count / analytics.actionBreakdown[0].count) * 100}%`,
                      background: COLORS[i % COLORS.length],
                    }}
                  />
                </div>
                <span className="admin-bar-count">{a.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hidden Cards */}
        <div className="admin-card">
          <h3 className="admin-card-title">👁️‍🗨️ Most Hidden Cards</h3>
          {analytics.hiddenCards.length > 0 ? (
            <div className="admin-bar-list">
              {analytics.hiddenCards.map(c => (
                <div key={c.card} className="admin-bar-item">
                  <span className="admin-bar-label">{c.card}</span>
                  <span className="admin-bar-count">{c.count} user{c.count !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="admin-empty">All cards visible for all users</p>
          )}
        </div>

        {/* Engagement Dropoff */}
        <div className="admin-card">
          <h3 className="admin-card-title">📉 Engagement Dropoff (14+ days)</h3>
          {analytics.droppedOff.length > 0 ? (
            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Last Seen</th>
                    <th>Total Logins</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.droppedOff.map(u => (
                    <tr key={u.email}>
                      <td className="admin-user-name">{u.name}</td>
                      <td className="admin-td-light">{u.daysSince}d ago</td>
                      <td className="admin-td-num">{u.totalLogins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="admin-empty">No dropoffs detected</p>
          )}
        </div>
      </div>

      {/* Dashboard Access Management */}
      <DashboardAccessManager users={data?.users || []} currentUserEmail={currentUserEmail} />
    </div>
  );
};

const DashboardAccessManager = ({ users, currentUserEmail }) => {
  const isSuperAdmin = currentUserEmail === SUPERADMIN_EMAIL;
  const [localAccess, setLocalAccess] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});

  useEffect(() => {
    const map = {};
    users.forEach(u => {
      map[u.email] = u.dashboard_access || ['funnel'];
    });
    setLocalAccess(map);
  }, [users]);

  const toggleDashboard = (email, dashKey) => {
    setLocalAccess(prev => {
      const current = prev[email] || ['funnel'];
      const updated = current.includes(dashKey)
        ? current.filter(k => k !== dashKey)
        : [...current, dashKey];
      // Always keep funnel
      if (!updated.includes('funnel')) updated.unshift('funnel');
      return { ...prev, [email]: updated };
    });
  };

  const saveAccess = async (email) => {
    setSaving(prev => ({ ...prev, [email]: true }));
    const success = await setUserDashboardAccess(email, localAccess[email] || ['funnel']);
    setSaving(prev => ({ ...prev, [email]: false }));
    if (success) {
      setSaved(prev => ({ ...prev, [email]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [email]: false })), 2000);
    }
  };

  if (users.length === 0) return null;

  return (
    <div className="admin-section" style={{ marginTop: '1.5rem' }}>
      <h3 className="admin-section-title">Dashboard Access Management</h3>
      <p style={{ fontSize: '0.75rem', color: 'rgba(199, 210, 254, 0.5)', margin: '0 0 1rem' }}>
        Control which dashboards each user can see. Admins always have access to all dashboards.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table" style={{ width: '100%', fontSize: '0.78rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0.5rem', minWidth: '180px' }}>User</th>
              {DASHBOARD_OPTIONS.map(d => (
                <th key={d.key} style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.68rem', minWidth: '80px' }}>
                  {d.label}
                </th>
              ))}
              <th style={{ textAlign: 'center', padding: '0.5rem', minWidth: '70px' }}></th>
            </tr>
          </thead>
          <tbody>
            {users
              .sort((a, b) => (a.display_name || a.email).localeCompare(b.display_name || b.email))
              .map(user => {
              const access = localAccess[user.email] || ['funnel'];
              const isAdminUser = user.is_admin;
              const isThisSuperAdmin = user.email === SUPERADMIN_EMAIL;
              // Superadmin row is always locked to all access
              // Superadmin can edit other admins; regular admins cannot edit admins
              const canEdit = isThisSuperAdmin ? false : (isSuperAdmin || !isAdminUser);
              return (
                <tr key={user.email} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '0.45rem 0.5rem' }}>
                    <div style={{ color: 'rgba(199, 210, 254, 0.9)', fontWeight: 600, fontSize: '0.78rem' }}>
                      {user.display_name || user.email.split('@')[0]}
                      {isThisSuperAdmin && (
                        <span style={{
                          fontSize: '0.6rem', color: '#818CF8', background: 'rgba(129, 140, 248, 0.15)',
                          padding: '0.1rem 0.35rem', borderRadius: '3px', marginLeft: '0.4rem',
                        }}>Super Admin</span>
                      )}
                      {isAdminUser && !isThisSuperAdmin && (
                        <span style={{
                          fontSize: '0.6rem', color: '#FBBF24', background: 'rgba(251, 191, 36, 0.15)',
                          padding: '0.1rem 0.35rem', borderRadius: '3px', marginLeft: '0.4rem',
                        }}>Admin</span>
                      )}
                    </div>
                    <div style={{ color: 'rgba(199, 210, 254, 0.4)', fontSize: '0.68rem' }}>{user.email}</div>
                  </td>
                  {DASHBOARD_OPTIONS.map(d => (
                    <td key={d.key} style={{ textAlign: 'center', padding: '0.45rem 0.5rem' }}>
                      {!canEdit ? (
                        <span style={{ color: '#4ADE80', fontSize: '0.85rem' }}>&#10003;</span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={access.includes(d.key)}
                          onChange={() => toggleDashboard(user.email, d.key)}
                          disabled={d.key === 'funnel'}
                          style={{ cursor: d.key === 'funnel' ? 'default' : 'pointer', accentColor: '#00BEA8' }}
                        />
                      )}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', padding: '0.45rem 0.5rem' }}>
                    {canEdit && (
                      <button
                        onClick={() => saveAccess(user.email)}
                        disabled={saving[user.email]}
                        style={{
                          padding: '0.25rem 0.5rem', fontSize: '0.68rem', borderRadius: '4px',
                          border: 'none', cursor: 'pointer',
                          background: saved[user.email] ? 'rgba(74, 222, 128, 0.2)' : 'rgba(0, 190, 168, 0.2)',
                          color: saved[user.email] ? '#4ADE80' : '#00BEA8',
                        }}
                      >
                        {saving[user.email] ? '...' : saved[user.email] ? 'Saved' : 'Save'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPanel;
